/**
 * Standard ecommerce events for GA4 (via gtag.js, the per-site
 * `GlobalSiteTagID` property) and Meta Pixel (via fbq, when a `PixelID` is
 * configured). One call site feeds both destinations.
 *
 * These fire alongside the existing Firebase Analytics custom events
 * (eventNames.*): GA4's Monetization reports and Meta's ads
 * optimization/reporting only understand their respective standard event
 * names and payload shapes, so the custom events alone never feed them.
 *
 * All emitters no-op when the destination library isn't loaded and never
 * throw — analytics must not break the ordering flow.
 */

function gtagEvent(name, params) {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') {
    return;
  }
  try {
    window.gtag('event', name, params);
  } catch (e) {
    // never let analytics break the app
  }
}

/**
 * Meta Pixel standard event. `eventId` enables Meta-side deduplication when
 * the same logical event can fire from more than one code path (and matches
 * server events if a Conversions API is ever added).
 */
function fbqEvent(name, params, eventId) {
  if (typeof window === 'undefined' || typeof window.fbq !== 'function') {
    return;
  }
  try {
    if (eventId) {
      window.fbq('track', name, params, { eventID: eventId });
    } else {
      window.fbq('track', name, params);
    }
  } catch (e) {
    // never let analytics break the app
  }
}

function getCurrencyCode() {
  return (
    (typeof window !== 'undefined' &&
      window.storeConfigInitial?.currencyCode) ||
    undefined
  );
}

/**
 * Converts cart lines (currentOrder.menuItems shape) into the GA4 items
 * array. `payablePrice` is the line total after customizations, so unit
 * price is derived by dividing by quantity. Catalog items that were never
 * customized fall back to their first size's price.
 */
export function toGa4Items(menuItems) {
  if (!Array.isArray(menuItems)) return [];
  return menuItems.map((item) => {
    const quantity =
      Number(item?.selectedSizes?.[0]?.quantity ?? item?.quantity ?? 1) || 1;
    const lineTotal = Number(
      item?.payablePrice ?? item?.selectedSizes?.[0]?.price
    );

    const ga4Item = {
      item_id: item?._id,
      item_name:
        item?.isHalf && item?.nameHalf
          ? `${item.name} / ${item.nameHalf}`
          : item?.name,
      quantity,
    };
    if (item?.categoryId) ga4Item.item_category = item.categoryId;
    if (Number.isFinite(lineTotal)) {
      ga4Item.price = Number((lineTotal / quantity).toFixed(2));
    }
    return ga4Item;
  });
}

function sumItemsValue(menuItems) {
  if (!Array.isArray(menuItems)) return undefined;
  const total = menuItems.reduce(
    (acc, item) => acc + (Number(item?.payablePrice) || 0),
    0
  );
  return Number(total.toFixed(2));
}

/**
 * Meta Pixel product params derived from the same cart lines: top-level
 * `content_ids` plus a `contents` array of { id, quantity, item_price }.
 */
function toMetaContents(menuItems) {
  const items = toGa4Items(menuItems);
  return {
    content_type: 'product',
    content_ids: items.map((item) => item.item_id).filter(Boolean),
    contents: items.map((item) => ({
      id: item.item_id,
      quantity: item.quantity,
      ...(item.price !== undefined && { item_price: item.price }),
    })),
  };
}

/** Params shared by cart/checkout events derived from a currentOrder. */
function orderParams(order) {
  const value = Number(order?.payableAmount);
  return {
    currency: getCurrencyCode(),
    ...(Number.isFinite(value) && { value }),
    items: toGa4Items(order?.menuItems),
  };
}

/** User opened a menu item's detail view. */
export function trackViewItem(item) {
  if (!item) return;
  gtagEvent('view_item', {
    currency: getCurrencyCode(),
    items: toGa4Items([item]),
  });
  fbqEvent('ViewContent', {
    currency: getCurrencyCode(),
    content_name: item.name,
    ...toMetaContents([item]),
  });
}

/** Item(s) added to the cart. Accepts one or more cart lines. */
export function trackAddToCart(items) {
  const list = Array.isArray(items) ? items : [items];
  if (list.length === 0 || !list[0]) return;
  gtagEvent('add_to_cart', {
    currency: getCurrencyCode(),
    value: sumItemsValue(list),
    items: toGa4Items(list),
  });
  fbqEvent('AddToCart', {
    currency: getCurrencyCode(),
    value: sumItemsValue(list),
    ...toMetaContents(list),
  });
}

/** A cart line removed from the order. */
export function trackRemoveFromCart(item) {
  if (!item) return;
  gtagEvent('remove_from_cart', {
    currency: getCurrencyCode(),
    value: sumItemsValue([item]),
    items: toGa4Items([item]),
  });
}

/** User is looking at their cart. */
export function trackViewCart(order) {
  if (!order?.menuItems?.length) return;
  gtagEvent('view_cart', orderParams(order));
}

/** User confirmed the order and moved to checkout/payment. */
export function trackBeginCheckout(order) {
  if (!order?.menuItems?.length) return;
  gtagEvent('begin_checkout', orderParams(order));
  const value = Number(order.payableAmount);
  fbqEvent('InitiateCheckout', {
    currency: getCurrencyCode(),
    ...(Number.isFinite(value) && { value }),
    num_items: order.menuItems.length,
    ...toMetaContents(order.menuItems),
  });
}

/** User chose how to pay. paymentType: 'card' | 'cash' | 'free'. */
export function trackAddPaymentInfo(order, paymentType) {
  if (!order?.menuItems?.length) return;
  gtagEvent('add_payment_info', {
    ...orderParams(order),
    ...(paymentType && { payment_type: paymentType }),
  });
  const value = Number(order.payableAmount);
  fbqEvent('AddPaymentInfo', {
    currency: getCurrencyCode(),
    ...(Number.isFinite(value) && { value }),
    ...toMetaContents(order.menuItems),
  });
}

/**
 * Order paid / placed. `transactionId` (the order/preOrder id) deduplicates
 * on both destinations: GA4 uses `transaction_id`, Meta uses `eventID`. So
 * the same order reported from two code paths (payment success + returning
 * home with a placed order) counts once.
 */
export function trackPurchase(order, transactionId) {
  if (!order || !transactionId) return;
  gtagEvent('purchase', {
    ...orderParams(order),
    transaction_id: transactionId,
  });
  // Meta requires value + currency on Purchase; skip if either is missing
  // rather than sending a broken event.
  const value = Number(order.payableAmount);
  const currency = getCurrencyCode();
  if (Number.isFinite(value) && currency) {
    fbqEvent(
      'Purchase',
      {
        currency,
        value,
        ...toMetaContents(order.menuItems),
      },
      transactionId
    );
  }
}

/** Existing user completed sign-in (OTP verified flow). */
export function trackLogin(method = 'phone') {
  gtagEvent('login', { method });
}

/** New user completed registration (name form). */
export function trackSignUp(method = 'phone') {
  gtagEvent('sign_up', { method });
  fbqEvent('CompleteRegistration', { status: true });
}
